// components/Common/DynamicButton.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { Button, CircularProgress } from '@mui/material';

interface DynamicButtonProps {
  text?: string;
  children?: React.ReactNode;
  route?: string;
  onClick?: () => void;
  loading?: boolean;
  loadingText?: string;
  disabled?: boolean;
  variant?: 'contained' | 'outlined' | 'text';
  fullWidth?: boolean;
  size?: 'small' | 'medium' | 'large';
  type?: 'button' | 'submit' | 'reset';
  className?: string;
  sx?: any;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
}

const DynamicButton: React.FC<DynamicButtonProps> = ({
  text,
  children,
  route,
  onClick,
  loading = false,
  loadingText,
  disabled = false,
  variant = 'contained',
  fullWidth = false,
  size = 'medium',
  type = 'button',
  className = '',
  sx = {},
  icon,
  iconPosition = 'start',
}) => {
  const buttonContent = (
    <Button
      type={type}
      variant={variant}
      fullWidth={fullWidth}
      size={size}
      onClick={onClick}
      disabled={disabled || loading}
      className={className}
      startIcon={icon && iconPosition === 'start' ? icon : undefined}
      endIcon={icon && iconPosition === 'end' ? icon : undefined}
      sx={{
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 2,
        px: size === 'small' ? 3 : size === 'large' ? 6 : 4,
        py: size === 'small' ? 1 : size === 'large' ? 1.5 : 1.2,
        ...(variant === 'contained' && {
          bgcolor: '#3B82F6',
          color: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          '&:hover': {
            bgcolor: '#2563EB',
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-in-out',
          },
          '&.Mui-disabled': {
            bgcolor: '#E2E8F0',
            color: '#94A3B8',
          },
        }),
        ...(variant === 'outlined' && {
          bgcolor: 'transparent',
          color: '#3B82F6',
          borderColor: '#3B82F6',
          borderWidth: 2,
          '&:hover': {
            bgcolor: 'rgba(59,130,246,0.04)',
            borderColor: '#2563EB',
            transform: 'scale(1.02)',
            transition: 'transform 0.2s ease-in-out',
          },
          '&.Mui-disabled': {
            borderColor: '#E2E8F0',
            color: '#94A3B8',
          },
        }),
        ...(variant === 'text' && {
          color: '#3B82F6',
          '&:hover': {
            bgcolor: 'rgba(59,130,246,0.04)',
          },
          '&.Mui-disabled': {
            color: '#94A3B8',
          },
        }),
        ...sx,
      }}
    >
      {loading ? (
        <>
          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
          {loadingText || text || children}
        </>
      ) : (
        text || children
      )}
    </Button>
  );

  if (route) {
    return (
      <Link to={route} style={{ textDecoration: 'none' }}>
        {buttonContent}
      </Link>
    );
  }

  return buttonContent;
};

export default DynamicButton;